import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileIcon from "@mui/icons-material/InsertDriveFile";

const CATEGORIES = {
  Leadership_and_Governance: "Leadership and Governance",
  Quality_Assurance: "Quality Assurance",
  Resource_Management: "Resource Management",
  Teaching_and_Learning: "Teaching and Learning",
  Student_Services: "Student Services",
  External_Relations: "External Relations",
  Research: "Research",
};

function ClassificationResultDialog({
  open,
  result,
  onConfirmChoice,
  onClose,
  isLoadingNextResult,
  files,
  selectedSinglePageFileIds,
}) {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [confidence, setConfidence] = useState(null);

  useEffect(() => {
    if (result) {
      // Use predicted_category from new result format
      const predictedCategory = result.predicted_category || "";
      setSelectedCategory(predictedCategory);

      // Generate random confidence between 0.75 and 1.0 for demo
      const randomConfidence = 0.75 + Math.random() * 0.25;
      setConfidence(randomConfidence);
    }
  }, [result]);

  if (!result) return null;

  // Derive selectedFile from files and selectedSinglePageFileIds
  const selectedFile =
    files && selectedSinglePageFileIds && selectedSinglePageFileIds.length > 0
      ? files.find((file) => file.id === selectedSinglePageFileIds[0])
      : null;

  const isPdf = selectedFile?.type === "application/pdf";
  const previewURL = isPdf
    ? selectedFile.downloadPDFURL
    : selectedFile?.downloadURL;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "1.5rem",
        }}
      >
        DOCUMENT CLASSIFICATION RESULT
      </DialogTitle>
      <DialogContent>
        {selectedCategory && (
          <Grid container spacing={3}>
            {/* Left Column - File Info and Preview */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                position: "relative",
              }}
            >
              {isLoadingNextResult && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    bgcolor: "rgba(255,255,255,0.7)",
                    zIndex: 10,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1,
                  bgcolor: "#f5f5f5",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                {previewURL ? (
                  <Box
                    component="img"
                    src={previewURL}
                    alt={selectedFile?.name}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 1,
                      bgcolor: "#f5f5f5",
                    }}
                  />
                ) : (
                  <FileIcon sx={{ fontSize: 100, color: "primary.main" }} />
                )}
              </Box>
              <Typography
                variant="h6"
                align="center"
                color="primary"
                gutterBottom
              >
                {selectedCategory}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2">File Details</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">
                  Name:{" "}
                  {selectedFile ? selectedFile.name : result.fileName || "N/A"}
                </Typography>
                <Typography variant="body2">
                  Size:{" "}
                  {selectedFile ? (selectedFile.size / 1024).toFixed(2) : "N/A"}{" "}
                  KB
                </Typography>
                <Typography variant="body2">
                  Type: {selectedFile ? selectedFile.type : "N/A"}
                </Typography>
              </Paper>
            </Grid>

            {/* Middle Column - Classification Details */}
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                <Typography variant="subtitle1" gutterBottom>
                  Classification Confidence
                </Typography>
                <Box
                  sx={{
                    height: 20,
                    bgcolor: "grey.300",
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${(confidence * 100).toFixed(2)}%`,
                      bgcolor:
                        confidence >= 0.8
                          ? "success.main"
                          : confidence >= 0.6
                          ? "warning.main"
                          : "error.main",
                      borderRadius: 1,
                    }}
                  />
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color:
                      confidence >= 0.8
                        ? "success.main"
                        : confidence >= 0.6
                        ? "warning.main"
                        : "error.main",
                    fontWeight: "bold",
                    mb: 2,
                  }}
                >
                  {(confidence * 100).toFixed(2)}% -{" "}
                  {confidence >= 0.8
                    ? "High Confidence"
                    : confidence >= 0.6
                    ? "Medium Confidence"
                    : "Low Confidence"}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Category Probabilities
                </Typography>
                <List dense>
                  <Typography variant="body2" color="textSecondary">
                    No category probabilities available.
                  </Typography>
                </List>
              </Paper>
            </Grid>

            {/* Right Column - Generate Summary Button */}
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                <Typography variant="subtitle1" gutterBottom>
                  Summary
                </Typography>
                <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}
                  >
                    {result.summary || "No summary generated"}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", px: 3, py: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon />}
          onClick={() => {
            if (selectedCategory) {
              onConfirmChoice(selectedCategory);
            }
          }}
          sx={{ minWidth: 200 }}
        >
          Confirm Choice
        </Button>
        <Box
          sx={{
            minWidth: 200,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Select
            fullWidth
            displayEmpty
            value={selectedCategory}
            renderValue={(val) => val || "Re-Assign"}
            onChange={(e) => {
              const label = e.target.value;
              if (
                window.confirm(
                  `Are you sure you want to re-assign to "${label}"?`
                )
              ) {
                setSelectedCategory(label);
              }
            }}
            inputProps={{ "aria-label": "Select Accreditation Area" }}
            size="small"
          >
            <MenuItem value="" disabled>
              Re-Assign
            </MenuItem>
            {Object.values(CATEGORIES) // ["Leadership and Governance", ...]
              .map((label) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
          </Select>
        </Box>
        <Button variant="outlined" onClick={onClose} sx={{ minWidth: 200 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ClassificationResultDialog;
